/**
 * HelloWorldTask — interactive component for triggering a Celery hello-world task.
 *
 * Displays a "Run Hello World Task" button. On click, dispatches a Celery task
 * via the FastAPI backend, polls the task status, and dynamically displays
 * the result (output string, timestamp, worker ID, execution duration)
 * without a full page refresh.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/shared/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/ui/card";
import { Loader2, Play, CheckCircle2, XCircle, Clock, Server, Timer } from "lucide-react";
import type { ApiFn } from "@/shared/lib/types";

interface HelloWorldTaskProps {
  api: ApiFn;
}

interface TaskDispatchResponse {
  task_id: string;
  status: string;
}

interface TaskStatusResponse {
  task_id: string;
  status: string;
  result: {
    output: string;
    timestamp: string;
    worker_id: string;
    duration_ms: number;
  } | null;
  error: string | null;
}

type TaskState = "idle" | "dispatching" | "polling" | "success" | "error";

const POLL_INTERVAL_MS = 1500;
const POLL_TIMEOUT_MS = 60_000;

export function HelloWorldTask({ api }: HelloWorldTaskProps) {
  const [taskState, setTaskState] = useState<TaskState>("idle");
  const [taskResult, setTaskResult] = useState<TaskStatusResponse["result"] | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef<number>(0);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const pollStatus = useCallback(
    async (taskId: string) => {
      try {
        const status = await api<TaskStatusResponse>(
          "GET",
          `/api/v1/tasks-demo/hello-world/${taskId}`,
        );

        if (status.status === "SUCCESS") {
          stopPolling();
          setTaskResult(status.result);
          setTaskState("success");
          toast.success("Hello World task completed!");
          return;
        }

        if (status.status === "FAILURE") {
          stopPolling();
          setErrorMsg(status.error || "Task failed");
          setTaskState("error");
          toast.error("Hello World task failed");
          return;
        }

        // Still pending/started — keep polling
        const elapsed = Date.now() - startTimeRef.current;
        if (elapsed > POLL_TIMEOUT_MS) {
          stopPolling();
          setErrorMsg("Task timed out after 60 seconds");
          setTaskState("error");
          toast.error("Task timed out");
        }
      } catch {
        // Network error during poll — keep polling until timeout
        const elapsed = Date.now() - startTimeRef.current;
        if (elapsed > POLL_TIMEOUT_MS) {
          stopPolling();
          setErrorMsg("Polling failed: timeout exceeded");
          setTaskState("error");
        }
      }
    },
    [api, stopPolling],
  );

  const handleRunTask = useCallback(async () => {
    setTaskState("dispatching");
    setTaskResult(null);
    setErrorMsg(null);

    try {
      const dispatch = await api<TaskDispatchResponse>(
        "POST",
        "/api/v1/tasks-demo/hello-world",
      );

      setTaskState("polling");
      startTimeRef.current = Date.now();

      // Start polling
      pollStatus(dispatch.task_id);
      pollRef.current = setInterval(() => {
        pollStatus(dispatch.task_id);
      }, POLL_INTERVAL_MS);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to dispatch task";
      setErrorMsg(message);
      setTaskState("error");
      toast.error(message);
    }
  }, [api, pollStatus]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  const isBusy = taskState === "dispatching" || taskState === "polling";

  return (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle className="text-lg font-semibold text-foreground">
          Celery Task Demo
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Click the button below to dispatch a <code className="text-foreground">hello_world</code> Celery task.
          The task executes on an available worker and returns a payload with the output string,
          server-side timestamp, worker ID, and execution duration.
        </p>

        <Button
          onClick={handleRunTask}
          disabled={isBusy}
          className="w-full sm:w-auto"
        >
          {isBusy ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {taskState === "dispatching" ? "Dispatching..." : "Waiting for result..."}
            </>
          ) : (
            <>
              <Play className="mr-2 h-4 w-4" />
              Run Hello World Task
            </>
          )}
        </Button>

        {taskState === "success" && taskResult && (
          <div className="rounded-lg border border-border bg-card p-4 space-y-3">
            <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
              <CheckCircle2 className="h-5 w-5" />
              <span className="font-medium">Task completed successfully</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div className="space-y-1">
                <span className="text-muted-foreground">Output</span>
                <p className="font-mono text-foreground bg-muted/50 px-2 py-1 rounded">
                  {taskResult.output}
                </p>
              </div>
              <div className="space-y-1">
                <span className="text-muted-foreground flex items-center gap-1">
                  <Clock className="h-3 w-3" /> Timestamp
                </span>
                <p className="font-mono text-foreground text-xs">
                  {taskResult.timestamp}
                </p>
              </div>
              <div className="space-y-1">
                <span className="text-muted-foreground flex items-center gap-1">
                  <Server className="h-3 w-3" /> Worker ID
                </span>
                <p className="font-mono text-foreground text-xs">
                  {taskResult.worker_id}
                </p>
              </div>
              <div className="space-y-1">
                <span className="text-muted-foreground flex items-center gap-1">
                  <Timer className="h-3 w-3" /> Duration
                </span>
                <p className="font-mono text-foreground">
                  {taskResult.duration_ms} ms
                </p>
              </div>
            </div>
          </div>
        )}

        {taskState === "error" && (
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4">
            <div className="flex items-center gap-2 text-destructive">
              <XCircle className="h-5 w-5" />
              <span className="font-medium">Task failed</span>
            </div>
            <p className="text-sm text-muted-foreground mt-1">{errorMsg}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
